// Vista Kanban de escritorio e iPad horizontal (D-043): columnas con scroll propio, y arrastrar
// una tarjeta a otra columna le cambia el Status. Con mouse se arrastra al moverlo unos
// píxeles; con el dedo, manteniendo presionado (así deslizar sigue haciendo scroll).

import { useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

export interface KanbanColumn<T> {
  key: string;
  label: string;
  cards: T[];
  /** Parte plegada (angosta); se abre al tocarla. */
  folded?: boolean;
  /** Clase de color del borde (grupo del Status). */
  tone: string;
}

interface Props<T extends { id: string }> {
  label: string;
  columns: KanbanColumn<T>[];
  /** Contenido de la tarjeta (sin el <li>): enlace y botón de horas. */
  renderCard: (card: T) => ReactNode;
  cardName: (card: T) => string;
  onMove: (card: T, toColumn: string) => void;
  /** Tarjetas por columna que se muestran de una vez (las cerradas pueden ser miles). */
  pageSize?: number;
}

function Card({ id, column, children }: { id: string; column: string; children: ReactNode }) {
  const { setNodeRef, listeners, isDragging } = useDraggable({ id, data: { column } });
  return (
    <li
      ref={setNodeRef}
      className={`card-row kanban-item${isDragging ? ' kanban-item-dragging' : ''}`}
      data-testid="kanban-card"
      {...listeners}
    >
      {children}
    </li>
  );
}

function Column<T extends { id: string }>({
  column,
  renderCard,
  pageSize,
}: {
  column: KanbanColumn<T>;
  renderCard: (card: T) => ReactNode;
  pageSize: number;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  const [open, setOpen] = useState(!column.folded);
  const [limit, setLimit] = useState(pageSize);
  const rest = column.cards.length - limit;
  const classes = ['kanban-column', column.tone];
  if (!open) classes.push('kanban-column-folded');
  if (isOver) classes.push('kanban-column-over');
  return (
    <section ref={setNodeRef} className={classes.join(' ')} aria-label={column.label}>
      <h2>
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span className="kanban-title">{column.label}</span>
          <span className="count">{column.cards.length}</span>
        </button>
      </h2>
      {open && (
        <ul className="kanban-cards">
          {column.cards.slice(0, limit).map((card) => (
            <Card key={card.id} id={card.id} column={column.key}>
              {renderCard(card)}
            </Card>
          ))}
          {column.cards.length === 0 && <li className="kanban-empty">Sin tarjetas</li>}
          {rest > 0 && (
            <li>
              <button
                type="button"
                className="button show-more"
                onClick={() => setLimit(limit + pageSize)}
              >
                Mostrar {Math.min(rest, pageSize)} más (quedan {rest})
              </button>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

export function Kanban<T extends { id: string }>({
  label,
  columns,
  renderCard,
  cardName,
  onMove,
  pageSize = 100,
}: Props<T>) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  const [active, setActive] = useState<T | null>(null);
  const find = (id: string | number) => {
    for (const col of columns) {
      const card = col.cards.find((c) => c.id === id);
      if (card) return { card, col };
    }
    return null;
  };
  const columnLabel = (id: string | number | undefined) =>
    columns.find((c) => c.key === id)?.label ?? '';
  const name = (id: string | number) => {
    const found = find(id);
    return found ? `«${cardName(found.card)}»` : 'la tarjeta';
  };

  const announcements: Announcements = {
    onDragStart: ({ active: a }) => `Moviendo ${name(a.id)}.`,
    onDragOver: ({ active: a, over }) =>
      over
        ? `${name(a.id)} sobre ${columnLabel(over.id)}.`
        : `${name(a.id)} fuera de las columnas.`,
    onDragEnd: ({ active: a, over }) =>
      over ? `${name(a.id)} quedó en ${columnLabel(over.id)}.` : `${name(a.id)} no se movió.`,
    onDragCancel: ({ active: a }) => `${name(a.id)} no se movió.`,
  };

  function onDragStart(e: DragStartEvent) {
    setActive(find(e.active.id)?.card ?? null);
  }

  function onDragEnd(e: DragEndEvent) {
    setActive(null);
    const found = find(e.active.id);
    const to = e.over?.id;
    if (!found || to === undefined || to === found.col.key) return;
    onMove(found.card, String(to));
  }

  return (
    <DndContext
      sensors={sensors}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable: 'Para cambiar el Status con el teclado, abre la tarjeta y usa «Más opciones».',
        },
      }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActive(null)}
    >
      <div className="kanban" role="group" aria-label={label}>
        {columns.map((column) => (
          <Column key={column.key} column={column} renderCard={renderCard} pageSize={pageSize} />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {active && <div className="card-row kanban-item kanban-overlay">{renderCard(active)}</div>}
      </DragOverlay>
    </DndContext>
  );
}
